// bm-design-system: alert primitive
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const alertVariants = cva("alert", {
  variants: {
    tone: {
      info: "alert-info",
      success: "alert-success",
      warning: "alert-warning",
      danger: "alert-danger",
    },
  },
  defaultVariants: {
    tone: "info",
  },
});

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof alertVariants> {
  /** Optional leading icon (e.g. a lucide icon element). */
  icon?: React.ReactNode;
  /** Optional bold title rendered above the body content. */
  title?: React.ReactNode;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, tone, icon, title, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ tone, className }))}
        {...props}
      >
        {icon && <span className="alert-icon">{icon}</span>}
        <div className="min-w-0 flex-1">
          {title && <p className="alert-title">{title}</p>}
          {children}
        </div>
      </div>
    );
  },
);
Alert.displayName = "Alert";

export { Alert, alertVariants };
